import { useSession } from '../../store/session';
import type { ThemeMode } from '../../theme/theme';
import { ru } from '../../strings/ru';
import { useReminderPrefs } from './useReminderPrefs';
import { isReminderApiAvailable } from '../../telegram/prefs';
import styles from './SettingsScreen.module.css';

const THEME_OPTIONS: Array<{ value: ThemeMode; label: string }> = [
  { value: 'auto', label: ru.settings.themeAuto },
  { value: 'morning', label: ru.settings.themeMorning },
  { value: 'evening', label: ru.settings.themeEvening },
];

export function SettingsScreen() {
  const settings = useSession((s) => s.settings);
  const setSetting = useSession((s) => s.setSetting);
  const closeSettings = useSession((s) => s.closeSettings);

  return (
    <div className={styles.screen}>
      <header className={styles.header}>
        <button className={styles.back} onClick={closeSettings}>
          ← Назад
        </button>
        <h1 className={styles.title}>{ru.settings.title}</h1>
      </header>

      <section className={styles.section}>
        <h2 className={styles.label}>{ru.settings.theme}</h2>
        <div className={styles.segmented}>
          {THEME_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={styles.segment}
              data-active={settings.themeMode === opt.value}
              onClick={() => setSetting('themeMode', opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.label}>
          {ru.settings.arabicSize}
          <span className={styles.value}>
            {Math.round(settings.arabicScale * 100)}%
          </span>
        </h2>
        <input
          className={styles.slider}
          type="range"
          min="0.85"
          max="1.6"
          step="0.05"
          value={settings.arabicScale}
          onChange={(e) => setSetting('arabicScale', Number(e.target.value))}
        />
        <p className={styles.preview} lang="ar" dir="rtl">
          سُبْحَانَ اللَّهِ وَبِحَمْدِهِ
        </p>
      </section>

      <section className={styles.section}>
        <Toggle
          label={ru.settings.showTranslit}
          checked={settings.showTranslit}
          onChange={(v) => setSetting('showTranslit', v)}
        />
        <Toggle
          label={ru.settings.showTranslation}
          checked={settings.showTranslation}
          onChange={(v) => setSetting('showTranslation', v)}
        />
      </section>

      {isReminderApiAvailable() && <RemindersSection />}
    </div>
  );
}

function RemindersSection() {
  const { prefs, update, save, status } = useReminderPrefs();

  return (
    <section className={styles.section}>
      <h2 className={styles.label}>{ru.settings.reminders}</h2>

      <TimeRow
        label={ru.reminders.morning}
        value={prefs.morningTime}
        onChange={(v) => update({ morningTime: v })}
      />
      <TimeRow
        label={ru.reminders.evening}
        value={prefs.eveningTime}
        onChange={(v) => update({ eveningTime: v })}
      />

      <button className={styles.saveBtn} onClick={save} disabled={status === 'saving'}>
        {status === 'saving' && 'Сохранение…'}
        {status === 'saved' && 'Сохранено ✓'}
        {status === 'error' && 'Не удалось сохранить, повторите'}
        {status === 'idle' && 'Сохранить'}
      </button>
    </section>
  );
}

function TimeRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  return (
    <label className={styles.toggleRow}>
      <span>{label}</span>
      <input
        type="time"
        className={styles.timeInput}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
      />
    </label>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className={styles.toggleRow}>
      <span>{label}</span>
      <input
        type="checkbox"
        className={styles.toggle}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}
